import type { FunctionComponent } from "react";
import { useEffect } from "react";
import { graphql, useQueryLoader } from "react-relay";

import { ClientSideSuspense } from "~/components/common/client-side-suspense";
import type { DigitalArchiveLinkProps } from "~/components/content/content-schema-render/link/digital-archive-link";
import { DigitalArchiveLinkImage } from "~/components/content/content-schema-render/link/digital-archive-link/digital-archive-link-image";
import { ImageSizeStyle, useContentUtils } from "~/lib/hooks";
import { isValidUrl } from "~/lib/utils";
import type { digitalArchiveLinkStyleArticleCheckPathQuery } from "~/queries/__generated__/digitalArchiveLinkStyleArticleCheckPathQuery.graphql";

import { DigitalArchiveLinkTopic } from "./digital-archive-link-topic";
import {
  ImageContainer,
  LeftContainer,
  RightContainer,
  RootContainer,
  Title,
  TitleContainer,
  Topic,
} from "./styles";

export const query = graphql`
  query digitalArchiveLinkStyleArticleCheckPathQuery($url: String!, $imageSize: String!) {
    checkPath(url: $url) {
      ...digitalArchiveLinkImageUrlPath
      ...digitalArchiveLinkUpdatedDateUrlPath
      ...digitalArchiveLinkTopicUrlPath
    }
  }
`;

export type Props = {
  href?: string;
  lazyload?: boolean;
} & DigitalArchiveLinkProps;

export const DigitalArchiveLink: FunctionComponent<Props> = ({
  href = "",
  lazyload,
  schemaNode,
}) => {
  const [queryReference, loadQuery, disposeQuery] =
    useQueryLoader<digitalArchiveLinkStyleArticleCheckPathQuery>(query);

  const { extractTextFromSchemaNodes } = useContentUtils();

  useEffect(() => {
    if (!isValidUrl(href)) return;

    const url = new URL(href);
    loadQuery({ imageSize: ImageSizeStyle["768x768"], url: url.pathname });

    return disposeQuery;
  }, [loadQuery, disposeQuery, href]);

  return (
    <RootContainer>
      <LeftContainer>
        <Topic>
          <ClientSideSuspense>
            {queryReference && <DigitalArchiveLinkTopic queryReference={queryReference} />}
          </ClientSideSuspense>
        </Topic>
        <TitleContainer>
          <Title>{extractTextFromSchemaNodes(schemaNode)}</Title>
        </TitleContainer>
      </LeftContainer>

      <RightContainer>
        <ImageContainer>
          <ClientSideSuspense>
            {queryReference && (
              <DigitalArchiveLinkImage lazyload={lazyload} queryReference={queryReference} />
            )}
          </ClientSideSuspense>
        </ImageContainer>
      </RightContainer>
    </RootContainer>
  );
};

DigitalArchiveLink.displayName = "DigitalArchiveLink";
